/*******************************************************************************
* @license
* Copyright (c) 2016 - present Accedo. All Rights Reserved.
* 
* This source code, its use, and distribution, is subject to the terms
* and conditions of the applicable license agreement.
* 
* A Vikimap service module to use for a Vikimap driven application. Supports Accedo One, JSON and Middleware services. (@accedo/vdkweb-vikimap) 4.0.8
* 
*******************************************************************************/
"use strict";

function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _entries = require("./utils/entries");
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor); } }
function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }
function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var mapEntry = function mapEntry(entryMapper, options) {
  return function (entry) {
    return entryMapper ? entryMapper(entry, options) : entry;
  };
};
var mapEntries = function mapEntries(entryMapper, options) {
  return function (entries) {
    return entryMapper ? Promise.all(entries.map(function (e) {
      return entryMapper(e, options);
    })) : entries;
  };
};
var VikimapService = /*#__PURE__*/_createClass(function VikimapService(entryProvider) {
  var _this = this;
  _classCallCheck(this, VikimapService);
  /**
   * Retrieves an entry by ID.
   *
   * @param {string} id              The ID for the entry to fetch
   * @param  {object} options        Custom options to be passed to entry provider
   * @param {function} entryMapper   Function for mapping the entry to a specific entry type
   * @return {Promise}               A promise resolving with the entry
   */
  _defineProperty(this, "getEntryById", function (id, options, entryMapper) {
    return _this._entryProvider.getEntryById(id, options).then(mapEntry(entryMapper, options));
  });
  /**
   * Retrieves a list of entries by an array of IDS.
   *
   * @param  {array} ids             An array of IDs for the entries to fetch
   * @param  {object} options        Custom options to be passed to entry provider
   * @param {function} entryMapper   Function for mapping the entry to a specific entry type
   * @return {Promise}               A promise resolving with the list of entries
   */
  _defineProperty(this, "getEntriesByIds", function (ids, options, entryMapper) {
    return _this._entryProvider.getEntriesByIds(ids, options).then(mapEntries(entryMapper, options));
  });
  _defineProperty(this, "getEntryByAlias", function (alias, options, entryMapper) {
    return _this._entryProvider.getEntryByAlias(alias, options).then(mapEntry(entryMapper, options));
  });
  _defineProperty(this, "getEntriesByAliases", function (aliases, options, entryMapper) {
    return _this._entryProvider.getEntriesByAliases(aliases, options).then(mapEntries(entryMapper, options));
  });
  _defineProperty(this, "getAllEntries", function (options, entryMapper) {
    return _this._entryProvider.getAllEntries(options).then(mapEntries(entryMapper, options));
  });
  _defineProperty(this, "expandListPropForEntry", function (entry, propName, entryMapper, options) {
    if (!entry || !propName) {
      return Promise.resolve(entry);
    }
    var listOfIds = entry[propName];
    if (!listOfIds || listOfIds.length < 1) {
      return Promise.resolve(entry);
    }
    return _this.getEntriesByIds(listOfIds, options).then(function (subEntries) {
      return entryMapper ? Promise.all(subEntries.map(function (e) {
        return entryMapper(e, options);
      })) : subEntries;
    }).then(function (subEntries) {
      return _objectSpread(_objectSpread({}, entry), {}, _defineProperty({}, propName, subEntries));
    });
  });
  _defineProperty(this, "expandPropForEntry", function (entry, propName, entryMapper, options) {
    if (!entry || !propName) {
      return Promise.resolve(entry);
    }
    if (!entry[propName]) {
      return entry;
    }
    var id = entry[propName];
    return _this.getEntryById(id, options).then(function (subEntry) {
      return entryMapper ? entryMapper(subEntry, options) : subEntry;
    }).then(function (subEntry) {
      return _objectSpread(_objectSpread({}, entry), {}, _defineProperty({}, propName, subEntry));
    });
  });
  // eslint-disable-next-line class-methods-use-this
  _defineProperty(this, "mapToItem", function (entry) {
    var propsToMap = {
      displaytext: 'displayText'
    };
    var mappedEntry = (0, _entries.mapProps)(entry, propsToMap);
    return Promise.resolve(mappedEntry);
  });
  _defineProperty(this, "mapToContainer", function (entry, options) {
    var propsToMap = {
      displaytext: 'displayText'
    };
    var mappedEntry = (0, _entries.mapProps)(entry, propsToMap);
    return _this.expandListPropForEntry(mappedEntry, 'items', _this.mapToItem, options);
  });
  _defineProperty(this, "mapToPage", function (entry, options) {
    var propsToMap = {
      displaytext: 'displayText'
    };
    var mappedEntry = (0, _entries.mapProps)(entry, propsToMap);
    return _this.expandListPropForEntry(mappedEntry, 'containers', _this.mapToContainer, options);
  });
  _defineProperty(this, "mapToMenuItem", function (entry, options) {
    var propsToMap = {
      menuitems: 'menuItems',
      displaytext: 'displayText',
      requireauthentication: 'requiresAuthentication'
    };
    var mappedEntry = (0, _entries.mapProps)(entry, propsToMap);
    return _this.expandListPropForEntry(mappedEntry, 'menuItems', _this.mapToMenuItem, options).then(function (newEntry) {
      return _this.expandPropForEntry(newEntry, 'page', _this.mapToPage, options);
    });
  });
  _defineProperty(this, "mapToMenu", function (entry, options) {
    var propsToMap = {
      isglobal: 'isGlobal'
    };
    var mappedEntry = (0, _entries.mapProps)(entry, propsToMap);
    return _this.expandListPropForEntry(mappedEntry, 'items', _this.mapToMenuItem, options);
  });
  /**
   * Gets a menu entry with the given ID.
   * Automatically also fetches menu item entries if such are
   * specified for the menu.
   *
   * @param  {string} id The ID of the menu entry
   * @param  {object} options   Custom options to be passed to entry provider
   * @return {Promise}   Promise resolving with the menu object with
   *                     expanded menu item objects
   */
  _defineProperty(this, "getMenu", function (id, options) {
    return _this.getEntryById(id, options, _this.mapToMenu);
  });
  /**
   * Gets menus based on the provided array of IDs.
   *
   * @param  {array} ids  An array of IDs
   * @param  {object} options   Custom options to be passed to entry provider
   * @return {Promise}    Promise resolving with the
   *                      object including the retrieved menus
   */
  _defineProperty(this, "getMenusByIds", function (ids, options) {
    return _this.getEntriesByIds(ids, options, _this.mapToMenu);
  });
  /**
   * Gets a page entry with the given ID.
   * Automatically also fetches container entries if such are
   * specified for the page.
   *
   * @param  {string} id  The ID of the page entry
   * @param  {object} options   Custom options to be passed to entry provider
   * @return {Promise}    Promise resolving with the page object
   *                      with expanded page container objects
   */
  _defineProperty(this, "getPage", function (id, options) {
    return _this.getEntryById(id, options, _this.mapToPage);
  });
  _defineProperty(this, "getPageByAlias", function (alias, options) {
    return _this.getEntryByAlias(alias, options, _this.mapToPage);
  });
  _defineProperty(this, "getPagesByAliases", function (aliases, options) {
    return _this.getEntriesByAliases(aliases, options, _this.mapToPage);
  });
  /**
   * Gets pages based on the provided array of IDs.
   *
   * @param  {array} ids  An array of IDs
   * @param  {object} options   Custom options to be passed to entry provider
   * @return {Promise}    Promise resolving with the
   *                      object including the retrieved pages
   */
  _defineProperty(this, "getPagesByIds", function (ids, options) {
    return _this.getEntriesByIds(ids, options, _this.mapToPage);
  });
  /**
   * Gets an item with the given ID.
   *
   * @param  {string} id  The ID of the item entry
   * @param  {object} options   Custom options to be passed to entry provider
   * @return {Promise}    Promise resolving with the item object
   */
  _defineProperty(this, "getItem", function (id, options) {
    return _this.getEntryById(id, options, _this.mapToItem);
  });
  /**
   * Gets items based on the provided array of IDs.
   *
   * @param  {array} ids         An array of IDs
   * @param  {object} options    Custom options to be passed to entry provider
   * @return {Promise}           Promise resolving with the
   *                             object including the retrieved items
   */
  _defineProperty(this, "getItemsByIds", function (ids, options) {
    return _this.getEntriesByIds(ids, options, _this.mapToItem);
  });
  /**
   * Gets theme based on the provided alias.
   *
   * @param  {string} alias      Entry alias
   * @param  {object} options    Custom options to be passed to entry provider
   * @return {object}            Theme object
   */
  _defineProperty(this, "getThemeByAlias", function (alias, options) {
    return _this.getEntryByAlias(alias, options, _this.mapToPage).then(function (entry) {
      if (entry.theme) {
        return _this.expandPropForEntry(entry, 'theme', _this.mapToItem, options).then(function (expandedEntry) {
          return expandedEntry.theme;
        });
      }
      return undefined;
    });
  });
  this._entryProvider = entryProvider;
});
var _default = VikimapService;
exports["default"] = _default;